import { Button, Form, Modal, Skeleton, Spin, Tabs } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import React, { useMemo, useState } from 'react';
import { CATEGORY_TYPE_OPTIONS, DEFAULT_OPTION_UNIT } from '../../constants';
import { useCategoryList } from '../../hooks/categorySetup';
import {
  useAllConfigOptions,
  useCreateConfigOption,
  useDeleteConfigOption,
  useUpdateConfigOption,
  useUploadConfigOptionImage,
} from '../../hooks/configOption';
import { useProductList } from '../../hooks/productSetup';
import { IConfigOption, IConfigOptionForm } from '../../types/configOption';
import { exportAllOptionsToExcel } from '../../utils/exportOptionsToExcel';
import { CategoryOptionsTable } from './helper/categoryOptionsTable';
import { JsonFlowViewer } from './helper/jsonFlowViewer';
import { OptionForm } from './helper/optionForm';

// Bulk options template - will be created across different categories
// const BULK_OPTIONS_TEMPLATE = [
//   {
//     "dogma_name": "Yes",
//     "dogma_description": "Staff Bead Yes",
//     "dogma_optiontype": 47,
//     "dogma_inputtype": 3,
//     "dogma_pricemodifier": 0,
//     "dogma_value": "Yes",
//     "dogma_repeatperwindowframe": false,
//     "dogma_Category@odata.bind": "/dogma_categories(72fb9dd6-aaef-f011-8406-6045bd9ca84c)"
//   },
//   {
//     "dogma_name": "No",
//     "dogma_description": "Staff Bead No",
//     "dogma_optiontype": 47,
//     "dogma_inputtype": 3,
//     "dogma_pricemodifier": 0,
//     "dogma_value": "No",
//     "dogma_repeatperwindowframe": false,
//     "dogma_Category@odata.bind": "/dogma_categories(72fb9dd6-aaef-f011-8406-6045bd9ca84c)"
//   },
//   {
//     "dogma_name": "Yes",
//     "dogma_description": "Parting Bead Yes",
//     "dogma_optiontype": 48,
//     "dogma_inputtype": 3,
//     "dogma_pricemodifier": 0,
//     "dogma_value": "Yes",
//     "dogma_repeatperwindowframe": false,
//     "dogma_Category@odata.bind": "/dogma_categories(72fb9dd6-aaef-f011-8406-6045bd9ca84c)"
//   },
//   {
//     "dogma_name": "No",
//     "dogma_description": "Parting Bead No",
//     "dogma_optiontype": 48,
//     "dogma_inputtype": 3,
//     "dogma_pricemodifier": 0,
//     "dogma_value": "No",
//     "dogma_repeatperwindowframe": false,
//     "dogma_Category@odata.bind": "/dogma_categories(72fb9dd6-aaef-f011-8406-6045bd9ca84c)"
//   },
//   {
//     "dogma_name": "No",
//     "dogma_description": "No top light/fan light",
//     "dogma_optiontype": 33,
//     "dogma_inputtype": 5,
//     "dogma_pricemodifier": 0,
//     "dogma_value": "No",
//     "dogma_repeatperwindowframe": false,
//     "dogma_Category@odata.bind": "/dogma_categories(72fb9dd6-aaef-f011-8406-6045bd9ca84c)"
//   },
//   {
//     "dogma_name": "<850 X 900",
//     "dogma_description": "Small top light/fan light (+£145)",
//     "dogma_optiontype": 33,
//     "dogma_inputtype": 5,
//     "dogma_pricemodifier": 145,
//     "dogma_value": "<850 X 900",
//     "dogma_repeatperwindowframe": false,
//     "dogma_Category@odata.bind": "/dogma_categories(72fb9dd6-aaef-f011-8406-6045bd9ca84c)"
//   },
//   {
//     "dogma_name": ">850 X 900",
//     "dogma_description": "Large top light/fan light (+£230)",
//     "dogma_optiontype": 33,
//     "dogma_inputtype": 5,
//     "dogma_pricemodifier": 230,
//     "dogma_value": ">850 X 900",
//     "dogma_repeatperwindowframe": false,
//     "dogma_Category@odata.bind": "/dogma_categories(72fb9dd6-aaef-f011-8406-6045bd9ca84c)"
//   },
//   {
//     "dogma_name": "Width (mm)",
//     "dogma_description": "Width (mm)",
//     "dogma_optiontype": 33,
//     "dogma_inputtype": 2,
//     "dogma_pricemodifier": 0,
//     "dogma_value": "",
//     "dogma_repeatperwindowframe": false,
//     "dogma_Category@odata.bind": "/dogma_categories(72fb9dd6-aaef-f011-8406-6045bd9ca84c)"
//   },
//   {
//     "dogma_name": "Height (mm)",
//     "dogma_description": "Height (mm)",
//     "dogma_optiontype": 33,
//     "dogma_inputtype": 2,
//     "dogma_pricemodifier": 0,
//     "dogma_value": "",
//     "dogma_repeatperwindowframe": false,
//     "dogma_Category@odata.bind": "/dogma_categories(72fb9dd6-aaef-f011-8406-6045bd9ca84c)"
//   }
// ];

export const ConfigurationSetup: React.FC = () => {
  const [form] = Form.useForm<IConfigOptionForm>();
  const [showEditor, setShowEditor] = useState(false);
  const [editingOption, setEditingOption] = useState<IConfigOption | null>(null);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>('');
  const [showJSONViewer, setShowJSONViewer] = useState(false);

  // API Hooks
  const { data: categoriesData, isLoading: isCategoriesLoading } = useCategoryList();
  const { data: allOptionsData } = useAllConfigOptions();
  const { data: productsData } = useProductList({ queryParams: { current: 1, pageSize: 100 } });
  const createOption = useCreateConfigOption();
  const updateOption = useUpdateConfigOption();
  const deleteOption = useDeleteConfigOption();
  const uploadOptionImage = useUploadConfigOptionImage();

  /**
   * Handle option image upload and update option with image URL
   */
  const handleOptionImageUpload = async (optionId: string, optionImage: any, isCreating: boolean) => {
    if (!optionImage || optionImage.length === 0) {
      return null;
    }

    const file = optionImage[0];

    // Get the original file object
    const originFileObj = file.originFileObj;
    if (!originFileObj) {
      return null;
    }

    try {
      const contentType = originFileObj.type || 'image/png';
      const fileName = originFileObj.name || 'option-image.png';

      // Read as ArrayBuffer to preserve all original image properties (including PNG transparency)
      const imageBlob: ArrayBuffer = await (originFileObj as Blob).arrayBuffer();

      // Upload image to Azure
      const response = await uploadOptionImage.mutateAsync({
        imageData: imageBlob,
        optionGuid: optionId,
        fileName: fileName,
        contentType: contentType,
        categoryId: selectedCategoryId,
      });

      // Update option with image URL if response contains the URL
      if (response && response.imageUrl) {
        const payload: any = {
          dogma_picturebloburl: response.imageUrl,
        };

        // If creating, also activate the option in the same update call
        if (isCreating) {
          payload.statecode = 0;
        }

        await updateOption.mutateAsync({
          id: optionId,
          payload,
          categoryId: selectedCategoryId,
        });
      }

      return response;
    } catch (error) {
      throw error;
    }
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();
      const { optionImage, ...optionData } = values;

      let optionId: string;
      const isCreating = !editingOption;

      if (editingOption) {
        // Update existing option
        await updateOption.mutateAsync({
          id: editingOption.dogma_configurationoptionsid,
          payload: optionData,
          categoryId: selectedCategoryId,
        });
        optionId = editingOption.dogma_configurationoptionsid;
      } else {
        // Create new option
        const res: any = await createOption.mutateAsync({
          categoryId: selectedCategoryId,
          payload: optionData,
        });
        optionId = res.dogma_configurationoptionsid;
      }

      // Upload option image if provided
      await handleOptionImageUpload(optionId, optionImage, isCreating);

      setShowEditor(false);
      form.resetFields();
      setEditingOption(null);
    } catch (error) {
    }
  };

  const handleAddOption = (categoryId: string) => {
    setSelectedCategoryId(categoryId);
    setEditingOption(null);
    form.resetFields(); // Reset all fields first
    form.setFieldsValue({
      dogma_name: '',
      dogma_description: '',
      dogma_optiontype: undefined,
      dogma_inputtype: 5, // Single Select (Dropdown)
      dogma_pricemodifier: 0,
      dogma_optionunit: DEFAULT_OPTION_UNIT,
      dogma_value: '',
      dogma_repeatperwindowframe: false,
    });
    setShowEditor(true);
  };

  const handleEdit = (option: IConfigOption) => {
    setEditingOption(option);
    setSelectedCategoryId(option._dogma_category_value || option.dogma_categoryid || '');
    form.setFieldsValue({
      dogma_name: option.dogma_name,
      dogma_description: option.dogma_description,
      dogma_optiontype: option.dogma_optiontype,
      dogma_inputtype: option.dogma_inputtype,
      dogma_pricemodifier: option.dogma_pricemodifier,
      dogma_optionunit: option.dogma_optionunit ?? DEFAULT_OPTION_UNIT,
      dogma_value: option.dogma_value,
      dogma_repeatperwindowframe: option.dogma_repeatperwindowframe || false,
    });
    setShowEditor(true);
  };

  const handleDelete = async (optionId: string, categoryId: string) => {
    await deleteOption.mutateAsync({ id: optionId, categoryId });
  };

  // const handleCreateBulkOptions = async () => {
  //   setIsCreatingBulk(true);
  //   let totalCreated = 0;
  //   let totalFailed = 0;

  //   try {
  //     // Iterate through each option in the template
  //     for (const optionData of BULK_OPTIONS_TEMPLATE) {
  //       try {
  //         // Extract category ID from the OData binding
  //         const categoryBinding = optionData['dogma_Category@odata.bind'];
  //         const categoryIdMatch = categoryBinding.match(/\(([^)]+)\)/);
  //         const categoryId = categoryIdMatch ? categoryIdMatch[1] : '';

  //         if (!categoryId) {
  //           console.error(`Failed to extract category ID from ${categoryBinding}`);
  //           totalFailed++;
  //           continue;
  //         }

  //         await createOption.mutateAsync({
  //           categoryId: categoryId,
  //           payload: optionData as any,
  //         });
  //         totalCreated++;
  //       } catch (error) {
  //         console.error(`Failed to create option ${optionData.dogma_name}:`, error);
  //         totalFailed++;
  //       }
  //     }

  //     if (totalFailed === 0) {
  //       message.success(`Successfully created ${totalCreated} bulk options`);
  //     } else {
  //       message.warning(`Created ${totalCreated} options, ${totalFailed} failed`);
  //     }
  //   } catch (error) {
  //     message.error('Failed to create bulk options');
  //     console.error('Error creating bulk options:', error);
  //   } finally {
  //     setIsCreatingBulk(false);
  //   }
  // };

  const handleCancel = () => {
    setShowEditor(false);
    form.resetFields();
    setEditingOption(null);
  };

  // Sort categories based on CATEGORY_TYPE_OPTIONS order
  const sortedCategories = useMemo(() => {
    if (!categoriesData?.value) return [];

    return [...categoriesData.value].sort((a, b) => {
      const orderA = CATEGORY_TYPE_OPTIONS.findIndex(opt => opt.value === a.dogma_categorytype) ?? 999;
      const orderB = CATEGORY_TYPE_OPTIONS.findIndex(opt => opt.value === b.dogma_categorytype) ?? 999;
      return orderA - orderB;
    });
  }, [categoriesData?.value]);

  const handleExportAll = () => {
    const allOpts: IConfigOption[] = allOptionsData ?? [];

    // Group options by their category ID
    const optsByCategory = new Map<string, IConfigOption[]>();
    for (const opt of allOpts) {
      const catId = opt._dogma_category_value ?? opt.dogma_categoryid ?? '';
      if (!optsByCategory.has(catId)) optsByCategory.set(catId, []);
      optsByCategory.get(catId)!.push(opt);
    }

    const data = sortedCategories.map((cat) => ({
      categoryId: cat.dogma_categoryid,
      categoryName: cat.dogma_name ?? 'Unknown',
      options: optsByCategory.get(cat.dogma_categoryid) ?? [],
    }));

    exportAllOptionsToExcel(data, {
      allCategories: sortedCategories.map((c) => ({
        id: c.dogma_categoryid,
        name: c.dogma_name ?? '',
      })),
      allProducts: (productsData?.value ?? []).map((p: any) => ({
        id: p.productid,
        name: p.name ?? '',
      })),
    });
  };

  // Calculate total options that would be created
  // const totalBulkOptions = BULK_OPTIONS_TEMPLATE.length;

  const tabItems = useMemo(() =>
    sortedCategories.map((category) => ({
      key: category.dogma_categoryid,
      label: category.dogma_name,
      children: (
        <CategoryOptionsTable
          category={{
            id: category.dogma_categoryid,
            name: category.dogma_name,
            description: category.dogma_description,
            ...category
          }}
          onAddOption={handleAddOption}
          onEditOption={handleEdit}
          onDeleteOption={(optionId) => handleDelete(optionId, category.dogma_categoryid)}
        />
      ),
    })),
  [sortedCategories]);

  return (
    <>
      <div className='bg-white rounded-lg p-4'>
        <div className="mb-4 flex justify-between items-start">
          <div>
            <h2 className='text-lg font-semibold text-text-primary'>Configuration Options</h2>
            <p className='text-sm text-text-secondary'>
              Manage all options - these will be assigned to products in the Setup Wizard
            </p>
          </div>
          <Button onClick={() => {
            handleExportAll()
          }}>Export</Button>
          {/* Export All button hidden for now */}
        </div>

        {isCategoriesLoading ? (
          <div className="space-y-3 pt-2">
            {/* Tab bar shimmer */}
            <div className="flex gap-2">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton.Button key={i} active size="default" style={{ width: 100, borderRadius: 6 }} />
              ))}
            </div>
            {/* Table content shimmer */}
            <div className="border border-neutral-200 rounded-lg p-4">
              <Spin spinning={false}>
                <Skeleton active paragraph={{ rows: 6 }} />
              </Spin>
            </div>
          </div>
        ) : (
          <Tabs
            type="card"
            items={tabItems}
          />
        )}
      </div>

      <Modal
        title={editingOption ? 'Edit Option' : 'Add Option'}
        open={showEditor}
        onCancel={handleCancel}
        onOk={handleSave}
        okText="Save"
        cancelText="Cancel"
        width={800}
        confirmLoading={createOption.isPending || updateOption.isPending}
        styles={{
          body: {
            maxHeight: '70dvh',
            overflowY: 'auto',
            overflowX: 'hidden',
            paddingBottom: '24px',
          },
        }}
      >
        <Form
          form={form}
          layout="vertical"
          key={editingOption?.dogma_configurationoptionsid || 'new-option'}
        >
          <OptionForm />
        </Form>
      </Modal>

      <JsonFlowViewer
        open={showJSONViewer}
        onClose={() => setShowJSONViewer(false)}
      />
    </>
  );
};
